import type { Faction, Race, ResearchDomainId } from "@sins2-atlas/game-data";
import { RACES } from "@sins2-atlas/game-data";
import {
  type FactionSearch,
  type FactionSelection,
  factionsOfRace,
  parseFactionSelection,
  racesOf,
} from "./faction-selection";

// The tech URL contract (ADR 0002: everything in the URL — the schema is a public API).
// /tech?domain=<token>&subject=<id>, plus the site-wide `?faction=` the whole app carries.
//
// `faction` is NOT this page's own key any more (Issue 35, ADR 0010): it holds the Faction
// Selection, a SET of Factions every surface reads (lib/faction-selection.ts), and the tree
// is DERIVED from it rather than picked here. `domain` still uses the game's race-neutral
// domain ids (military/civilian — each race labels its own tabs: Military/Civilian,
// Hostility/Harmony, Warfare/Empire), and its default stays out of the URL so the bare
// /tech link is canonical.
//
// A tree belongs to ONE Race and the selection may name none, one or four, so the
// derivation always lands on a tree and REPORTS when it had to choose (ADR 0002 amendment
// 2026-07-30): the page must never render one tree under a URL naming something else
// without saying so.

const DOMAIN_TOKENS = ["military", "civilian"] as const satisfies readonly ResearchDomainId[];

const DEFAULT_DOMAIN: ResearchDomainId = "military";

/** The Race drawn when the selection cannot settle one on its own — the first in the
 * dataset's own Race order, which is the game's. */
const DEFAULT_RACE: Race = RACES[0];

/** Why this Race, when the Faction Selection did not settle it alone. Absent when the
 * selection names exactly one Race, which is the control working as intended. The wording
 * lives in the route; the decision lives here and is pinned by tests. */
export type TreeReason =
  /** The selection names more than one Race — All Factions included. A tree belongs to one
   * Race, so the first of them in Race order is drawn and the page says which and why. */
  | { kind: "multipleRaces"; races: Race[] }
  /** The selection names no Faction at all: a URL whose every token was unrecognized. The
   * tokens themselves are on `selection.unknown`. */
  | { kind: "noFaction" };

export interface TechSelection {
  /** The Race whose tree is drawn. */
  race: Race;
  /** Which of that Race's Factions the tree shows, in canonical order. Both of them is the
   * union — strictly more than either Faction sees in game, exclusives labelled. */
  factions: Faction[];
  domain: ResearchDomainId;
  /** The Selected Subject's id (ADR 0006), absent when nothing is selected. Carried
   * verbatim: this module knows tokens, not the dataset, so whether an id is real and which
   * tree owns it is `treeForSelection`'s job in lib/tech-detail.ts. */
  subjectId?: string;
  /** The site-wide selection this tree came from — what the page shows back to the reader
   * when it has to explain itself, unrecognized tokens included. */
  selection: FactionSelection;
  /** Why this Race, when the selection did not name exactly one. */
  reason?: TreeReason;
  /** The token the URL asked for that is not a domain, when the domain fell back to the
   * default. Present only when the URL actually named something unusable. */
  unknownDomain?: string;
}

export interface TechSearch extends FactionSearch {
  domain?: string;
  subject?: string;
}

export function parseTechSelection(search: TechSearch): TechSelection {
  const selection = parseFactionSelection(search);
  // Which Races a selection names is the selection's own rule, declared once in
  // faction-selection.ts — this module asks it rather than re-deriving it.
  const races = racesOf(selection);
  const race = races[0] ?? DEFAULT_RACE;
  // A selection naming no Faction at all (every token unrecognized) still gets a tree: the
  // default Race, WHOLE, because falling back to an empty tree would answer an unusable
  // link with a page that looks broken.
  const factions =
    races.length === 0
      ? factionsOfRace(race).map((entry) => entry.faction)
      : factionsOfRace(race)
          .filter((entry) => selection.factions.includes(entry.token))
          .map((entry) => entry.faction);
  const reason: TreeReason | undefined =
    races.length === 0
      ? { kind: "noFaction" }
      : races.length > 1
        ? { kind: "multipleRaces", races }
        : undefined;
  const requestedDomain = DOMAIN_TOKENS.find((token) => token === search.domain);
  const domain = requestedDomain ?? DEFAULT_DOMAIN;
  // An absent param is not an unusable one — only a param that named something report it.
  const unknownDomain =
    requestedDomain === undefined && search.domain !== undefined && search.domain !== ""
      ? search.domain
      : undefined;
  return {
    race,
    factions,
    domain,
    subjectId: search.subject,
    selection,
    ...(reason === undefined ? {} : { reason }),
    ...(unknownDomain === undefined ? {} : { unknownDomain }),
  };
}

/** Serialize the tree's own keys, omitting the default domain so the canonical bare /tech
 * URL stays bare. `faction` is not here: it is the site-wide selection, carried along every
 * link by `factionLinkSearch` exactly as it is on every other route. */
export function serializeTechSelection(selection: {
  domain: ResearchDomainId;
  subjectId?: string;
}): Omit<TechSearch, "faction"> {
  return {
    domain: selection.domain === DEFAULT_DOMAIN ? undefined : selection.domain,
    subject: selection.subjectId,
  };
}
